import { createMachine, assign } from 'xstate';
import { isOrderFlowItemStatus } from '@/types/orderStatus';
import { Order, OrderStep, SupervisorEvent } from '../types/supervisor';
import { store } from '../app/store';
import { selectIsAuthenticated } from '../features/auth/authSlice';
import { resetShippingQuote, selectHasMeaningfulItems } from '../features/order/orderSlice';
import { logging } from '../services/logging';

const isOrderedStatus = (value: unknown): boolean => isOrderFlowItemStatus(value);


// ===========================================================================
// Machine Context and Events
// ===========================================================================

export interface OrderMachineContext {
  order: Order;
  activeStep: OrderStep;
  completeness: Record<OrderStep, boolean>;
}

export const createInitialContext = (): OrderMachineContext => ({
  order: {
    items: [],
  },
  activeStep: 'upload',
  completeness: {
    upload: false,
    uploadedImages: false,
    size: false,
    printer: false,
    paper: false,
    frame: false,
    review: false,
    address: false,
    payment: false,
    login: false,
    checkingForPendingOrders: false,
    done: false,
  },
});

// ===========================================================================
// Machine Definition
// ===========================================================================

export const orderMachine = createMachine(
  {
    id: 'order',
    initial: 'idle',
    types: {} as {
      context: OrderMachineContext;
      events: SupervisorEvent;
    },
    context: createInitialContext(),
    on: {
      'AUTH.LOGGED_IN': {
        target: '.checkingForPendingOrders',
      },
      'AUTH.LOGGED_OUT': {
        target: '.idle',
        actions: ['resetContext'],
      },
      'ORDER.CLEAR_FOR_ORDER': {
        actions: ['clearItemsForDeletedOrder', 'persistLocalStorage', 'recomputeCompleteness', 'setActiveStepFromOrder'],
      },
    },
    states: {
      idle: {
        on: {
          'ORDER.APPLY_PATCH': [
            {
              target: 'login',
              guard: 'isAnonymous',
            },
            {
              target: 'screen',
              actions: ['applyPatchSafely', 'persistLocalStorage', 'recomputeCompleteness', 'setActiveStepFromOrder'],
            },
          ],
        },
      },
      login: {},
      checkingForPendingOrders: {
        invoke: {
          src: 'fetchPendingOrders',
          onDone: {
            target: 'screen',
            actions: [
              'applyPendingOrderResult',
              'persistLocalStorage',
              'announcePendingItems',
              'recomputeCompleteness',
            ],
          },
          onError: {
            target: 'screen',
            actions: 'logError',
          },
        },
      },
      screen: {
        initial: 'upload',
        states: {
          upload: { entry: 'ensureUploadReady' },
          uploadedImages: {},
          size: { entry: 'ensureSizeReady' },
          paper: { entry: 'ensurePapersLoaded' },
          printer: { entry: 'ensurePrintersLoaded' },
          frame: {},
          address: { entry: 'ensureAddressReady' },
          payment: { entry: 'ensurePaymentUi', exit: 'resetShippingQuoteState' },
          login: {},
          review: { entry: 'ensureSummary' },

          done: {},
        },
        // Eventless (transient) transitions. These are checked whenever the state is entered
        // or after an event updates the context.
        always: [
          { target: '.upload', guard: ({ context }) => context.activeStep === 'upload' },
          { target: '.uploadedImages', guard: ({ context }) => context.activeStep === 'uploadedImages' },
          { target: '.size', guard: ({ context }) => context.activeStep === 'size' },
          { target: '.paper', guard: ({ context }) => context.activeStep === 'paper' },
          { target: '.printer', guard: ({ context }) => context.activeStep === 'printer' },
          { target: '.frame', guard: ({ context }) => context.activeStep === 'frame' },
          { target: '.review', guard: ({ context }) => context.activeStep === 'review' },
          { target: '.address', guard: ({ context }) => context.activeStep === 'address' },
          { target: '.payment', guard: ({ context }) => context.activeStep === 'payment' },
          { target: '.login', guard: ({ context }) => context.activeStep === 'login' },
          { target: '.done', guard: ({ context }) => context.activeStep === 'done' },
        ],
        on: {
          'UI.GOTO_STEP': {
            // This is an internal transition: it only updates context, then 'always' will trigger the state change.
            actions: assign({
              activeStep: ({ event }) => event.step,
            }),
          },
          'ORDER.APPLY_PATCH': {
            // No target makes this an internal transition.
            actions: ['applyPatchSafely', 'persistLocalStorage', 'recomputeCompleteness'],
          },
          'UI.IMAGES_UPLOADED': {
            actions: assign({
              activeStep: () => 'uploadedImages' as OrderStep,
            }),
          },
          'UI.ANNOUNCE_UPLOAD_SUMMARY': {
            actions: ['notifyUploadSummary'],
          },
          'UI.SHOW_LOGIN': {
            actions: assign({
              activeStep: () => 'login' as OrderStep,
            }),
          },
          'UI.REQUEST_AI_PROMPT': {
            actions: ['handleAiPrompt'],
          },
        },
      },
    },
  },
  {
    actions: {
      resetContext: assign(() => createInitialContext()),
      setActiveStepLogin: assign({
        activeStep: () => 'login' as OrderStep,
      }),
      promptLogin: () => {
        const state = store.getState();
        if (!selectIsAuthenticated(state)) {
          logging.warn('User is not authenticated. Prompting login.');
        }
      },
      resetShippingQuoteState: () => {
        store.dispatch(resetShippingQuote());
      },
      setActiveStepFromOrder: assign(({ context, event }) => {
        if (event.type === 'ORDER.CLEAR_FOR_ORDER' && event.skipSetActiveStep) {
          return {
            activeStep: context.activeStep,
          } satisfies Partial<OrderMachineContext>;
        }

        const nextStep = (selectHasMeaningfulItems(store.getState()) ? 'review' : 'upload') as OrderStep;
        return {
          activeStep: nextStep,
        } satisfies Partial<OrderMachineContext>;
      }),
      clearItemsForDeletedOrder: assign(({ context, event }) => {
        if (event.type !== 'ORDER.CLEAR_FOR_ORDER') {
          return {};
        }

        const providedOrderId =
          typeof event.orderId === 'string' && event.orderId.trim().length > 0
            ? event.orderId.trim()
            : undefined;

        const currentOrder = context.order as Order & Record<string, unknown>;
        const currentItems = Array.isArray(currentOrder.items) ? currentOrder.items : [];

        const metadataOrderId =
          providedOrderId ??
          (typeof currentOrder.id === 'string' && currentOrder.id.length > 0
            ? (currentOrder.id as string)
            : undefined);

        const filteredItems = currentItems.filter((item) => {
          if (!item) {
            return false;
          }

          if (isOrderedStatus(item.status)) {
            return false;
          }

          if (!metadataOrderId) {
            return true;
          }

          const itemOrderId =
            typeof item.orderId === 'string' && item.orderId.length > 0 ? item.orderId : undefined;

          return itemOrderId !== metadataOrderId;
        });

        const base = createInitialContext();

        if (filteredItems.length === 0) {
          return {
            ...base,
            order: {
              ...base.order,
              id: undefined,
              status: undefined,
              shippingAddress: undefined,
            } as Order & { id?: string | null; status?: string | null },
          } satisfies OrderMachineContext;
        }

        const sanitizedOrder = {
          ...base.order,
          items: filteredItems.map((item) => ({
            ...item,
            orderId: item?.orderId ?? null,
          })),
          id: undefined,
          status: undefined,
          shippingAddress: undefined,
        } as Order & { id?: string | null; status?: string | null };

        return {
          ...base,
          order: sanitizedOrder,
        } satisfies OrderMachineContext;
      }),
    },
    guards: {
      isAuthenticated: () => selectIsAuthenticated(store.getState()),
      isAnonymous: () => !selectIsAuthenticated(store.getState()),
    },
  }
);
